/**
 * Набор тем организации (#845) — `/api/v1/analytics/voice/themes`.
 *
 * Каждый ответ разбирается схемой из `@aica/api-types`. Отказы (кап, дубль,
 * «тему изменили») приходят 409 с `code` — их читает `themeErrorMessage`
 * экрана, а не этот клиент: что сказать человеку, решает форма рядом с кнопкой.
 * Во встроенном Bitrix24 тот же путь переписывается в `/api/v1/embedded/*`.
 */
import {
  acceptOrgThemeProposalResponseSchema,
  approveThemeBootstrapResponseSchema,
  listOrgThemeProposalsResponseSchema,
  listOrgThemesResponseSchema,
  rejectOrgThemeProposalResponseSchema,
  orgThemeMutationResponseSchema,
  THEME_BOOTSTRAP_CLIENT_TIMEOUT_MS,
  themeBootstrapStateSchema,
  type AcceptOrgThemeProposalResponse,
  type ApproveThemeBootstrapRequest,
  type ApproveThemeBootstrapResponse,
  type CreateOrgThemeRequest,
  type ListOrgThemeProposalsResponse,
  type ListOrgThemesQuery,
  type ListOrgThemesResponse,
  type OrgThemeMutationResponse,
  type RejectOrgThemeProposalResponse,
  type ThemeBootstrapState,
  type UpdateOrgThemeRequest,
} from '@aica/api-types/schemas';
import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';

export class VoiceThemesApi extends BaseApiClient {
  /**
   * The set. `includeDemo: 'no'` — the screen declines the demo of a closed
   * section (#2816): the route then refuses below the plan instead of
   * answering the demo set. Absent — the demo opening may be answered.
   */
  list(
    params: Pick<ListOrgThemesQuery, 'includeDemo'> = {}
  ): Promise<ListOrgThemesResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.VOICE_THEMES}${this.buildQuery(params)}`,
      listOrgThemesResponseSchema
    );
  }

  create(body: CreateOrgThemeRequest): Promise<OrgThemeMutationResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.VOICE_THEMES,
      orgThemeMutationResponseSchema,
      { method: 'POST', body: JSON.stringify(body) }
    );
  }

  update(id: string, body: UpdateOrgThemeRequest): Promise<OrgThemeMutationResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.VOICE_THEME_BY_ID(id),
      orgThemeMutationResponseSchema,
      { method: 'PATCH', body: JSON.stringify(body) }
    );
  }

  archive(id: string): Promise<OrgThemeMutationResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.VOICE_THEME_ARCHIVE(id),
      orgThemeMutationResponseSchema,
      { method: 'POST' }
    );
  }

  unarchive(id: string): Promise<OrgThemeMutationResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.VOICE_THEME_UNARCHIVE(id),
      orgThemeMutationResponseSchema,
      { method: 'POST' }
    );
  }

  /** The starter proposal (#845 PR 4): a plain read, never starts anything. */
  bootstrapState(): Promise<ThemeBootstrapState> {
    return this.validatedRequest(
      API_ENDPOINTS.VOICE_THEME_BOOTSTRAP,
      themeBootstrapStateSchema
    );
  }

  /**
   * «Suggest themes from history» / «Suggest again» — answers the state after
   * it. A paid call, so WITHOUT retries (`maxRetries: 0`): a repeat is a new
   * press and a new slot of the daily ceiling. The generation runs on without
   * the caller (a Workflow), so the answer is fast (`running` once claimed); the
   * wait covers edge-api's own wait for workflows-api AND the state it reads
   * after it (`THEME_BOOTSTRAP_CLIENT_TIMEOUT_MS`), so the client reads edge-api's
   * answer instead of aborting first.
   */
  bootstrapGenerate(mode: 'start' | 'regenerate'): Promise<ThemeBootstrapState> {
    return this.validatedRequest(
      mode === 'start'
        ? API_ENDPOINTS.VOICE_THEME_BOOTSTRAP_START
        : API_ENDPOINTS.VOICE_THEME_BOOTSTRAP_REGENERATE,
      themeBootstrapStateSchema,
      {
        method: 'POST',
        signal: AbortSignal.timeout(THEME_BOOTSTRAP_CLIENT_TIMEOUT_MS),
      },
      0
    );
  }

  bootstrapApprove(
    body: ApproveThemeBootstrapRequest
  ): Promise<ApproveThemeBootstrapResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.VOICE_THEME_BOOTSTRAP_APPROVE,
      approveThemeBootstrapResponseSchema,
      { method: 'POST', body: JSON.stringify(body) }
    );
  }

  /** The weekly theme proposals (#845 PR 5b): the set's editors only. */
  proposals(): Promise<ListOrgThemeProposalsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.VOICE_THEME_PROPOSALS,
      listOrgThemeProposalsResponseSchema
    );
  }

  acceptProposal(id: string): Promise<AcceptOrgThemeProposalResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.VOICE_THEME_PROPOSAL_ACCEPT(id),
      acceptOrgThemeProposalResponseSchema,
      { method: 'POST' }
    );
  }

  rejectProposal(id: string): Promise<RejectOrgThemeProposalResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.VOICE_THEME_PROPOSAL_REJECT(id),
      rejectOrgThemeProposalResponseSchema,
      { method: 'POST' }
    );
  }
}
